import processorsTemplate from './processors.html';

/*******************************************************************************
 * @ngDoc     Component
 * @name      csm.books.OurOrg.component:processors
 * @description
 * This component displays the current list of processors in Our School, Our PTA
 * and Our Booster. Processors are added or updated using the addEditProcessor
 * service. Settings for the different processor types are passed in to this
 * component using the AppConfig provider. This allows certain elements to be
 * hiden/displayed for each org type.
 *******************************************************************************/
export const processorsComponent = {
  template: processorsTemplate,
  controller: processorsController
};

/*******************************************************************************
 * processorsController */
/**
 * Controller for the processors admin component.
 *******************************************************************************/
/* @ngInject */ function processorsController(
  $injector,
  $q,
  $scope,
  AppConfig,
  NewBooksData,
  Services,
  SERVER
) {
  var $ctrl = this;
  var $svcs = { $ctrl: $ctrl, $scope: $scope };
  $ctrl.settings = AppConfig.settings;

  $ctrl.addEditProcessor = $injector.invoke(addEditProcessor, null, $svcs);
  $ctrl.makeRowEntity = $injector.invoke(makeRowEntity, null, $svcs);
  $ctrl.gridOptions = $injector.invoke(buildGrid, null, $svcs);
  $ctrl.editable = $injector.invoke(editable, null, $svcs);
  $ctrl.openKpsMerchantDashboard = $injector.invoke(
    openKpsMerchantDashboard,
    null,
    $svcs
  );

  /*---------------------------------------------------*/
  /* Get the processors and map out all of the account */
  /* types for display in the processor dialog.        */
  /*---------------------------------------------------*/
  $q.all({
    processors: Services.getProcessors(),
    accounts: Services.getProcessorAccounts(),
    kpsFlags: Services.getKpsActiveFlags()
  }).then(function (r) {
    $ctrl.accounts = {
      bankAccts: r.accounts.bankAccts.map(
        (ba) => new NewBooksData.accounts.AccountAdminInfo(ba)
      ),
      procExpAccts: r.accounts.procExpAccts.map(
        (pa) => new NewBooksData.accounts.AccountAdminInfo(pa)
      ),
      feeIncAccts: r.accounts.feeIncAccts.map(
        (ia) => new NewBooksData.accounts.AccountAdminInfo(ia)
      ),
      noSaleAccts: r.accounts.noSaleAccts.map(
        (ra) => new NewBooksData.accounts.AccountAdminInfo(ra)
      ),
      undepAccts: r.accounts.undepAccts.map(
        (ua) => new NewBooksData.accounts.AccountAdminInfo(ua)
      )
    };

    $ctrl.gridOptions.data = r.processors.map($ctrl.makeRowEntity);

    $ctrl.kpsActive = r.kpsFlags.kpsActive;
    $ctrl.cpKpsActive = r.kpsFlags.cpKpsActive;

    $ctrl.gridApi.core.refresh();
  });
}

/*******************************************************************************
 * buildGrid */
/**
 * Builds the grid options for the processors grid.
 *******************************************************************************/
/* @ngInject */ function buildGrid(uiGridConstants, $ctrl, $scope) {
  var dblClickTemplate = `<div ng-dblclick="grid.appScope.$ctrl.addEditProcessor(row.entity, false)" 
                              ng-repeat="(colRenderIndex, col) in colContainer.renderedColumns track by col.colDef.name" 
                              class="ui-grid-cell" ui-grid-cell data-index="colRenderIndex"
                              ng-class="{'processor-inactive':!row.entity.active}"></div>`;

  var actionsCellTemplate = `<div class="ui-grid-cell-contents ng-binding ng-scope">
                              <span class="pointer glyphicon glyphicon-pencil btn btn-info btn-xs csm-grid-space-target" title="Edit Processor" 
                                    ng-click="grid.appScope.$ctrl.addEditProcessor(row.entity, false)" ng-show="grid.appScope.$ctrl.editable(row.entity)">
                              </span></span> &nbsp;</span>
                            </div>`;
  return {
    enableColumnMenus: false,
    enableRowHeaderSelection: false,
    enableCellEdit: false,
    enableColumnResizing: true,
    enableRowSelection: true,
    enableFullRowSelection: true,
    enableSorting: true,
    multiSelect: false,
    enableSelectAll: false,
    rowTemplate: dblClickTemplate,
    enableRowHashing: false,
    getRowIdentity: (row) => row.id,
    rowIdentity: (row) => row.id,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
    enableVerticalScrollbar: uiGridConstants.scrollbars.NEVER,
    minRowsToShow: 1,
    maxRowsToShow: 15,
    data: [],
    onRegisterApi: function (gridApi) {
      $ctrl.gridApi = gridApi;
      $scope.$on('shown.bs.tab', function () {
        $ctrl.gridApi.core.handleWindowResize();
      });
    },
    columnDefs: [
      {
        displayName: 'Processor',
        field: 'processorLabel',
        minWidth: 200
      },
      {
        displayName: 'Account Name',
        field: 'accountName',
        minWidth: 200
      },
      {
        displayName: 'Type',
        field: 'processorTypeLabel',
        minWidth: 200
      },
      {
        field: 'actions',
        displayName: 'Action',
        width: 80,
        cellTemplate: actionsCellTemplate,
        cellClass: 'center',
        headerCellClass: 'center',
        pinnedRight: true,
        enableSorting: false
      }
    ]
  };
}

/*******************************************************************************
 * addEditProcessor */
/**
 * Opens the addEditProcessor dialog to either edit or add a new processor.
 *
 * @param  processor  Processor to update.
 * @param  isAdd  `true`=Add new processor, `false`=Edit `processor`.
 *******************************************************************************/
/* @ngInject */ function addEditProcessor(
  $ctrl,
  AddEditProcessorDialog,
  Alert,
  SERVER
) {
  return function (processor, isAdd) {
    if (!$ctrl.accounts) return;
    if (processor && !$ctrl.editable(processor)) return;

    const manualProcessorExists = $ctrl.gridOptions.data.some(
      (processor) => processor.type === SERVER.BooksDBConstants.PT_MANUAL
    );

    /**
     * Only show this alert if manual processor exists and the user is attempting to add a new processor. This allows
     * processors to still be edited as well as have this restriction in place.
     */
    if (
      $ctrl.kpsActive &&
      $ctrl.cpKpsActive &&
      manualProcessorExists &&
      isAdd
    ) {
      Alert.info(
        `
        Card Present and Card Not Present KPS is active, you can no longer create new processors. 
        If you need to do this ask support to turn off Card Present KPS or Card Not Present KPS.
      `,
        'KPS Restriction',
        true
      );
      return;
    }

    AddEditProcessorDialog.show(
      $ctrl.accounts,
      processor,
      $ctrl.gridOptions.data,
      $ctrl.kpsActive,
      $ctrl.cpKpsActive
    ).then(function (p) {
      if (isAdd) {
        $ctrl.gridOptions.data.push($ctrl.makeRowEntity(p));
        $ctrl.gridApi.core.handleWindowResize();
      } else {
        var idx = $ctrl.gridOptions.data.indexOf(processor);
        $ctrl.gridOptions.data[idx] = $ctrl.makeRowEntity(p);
        $ctrl.gridApi.core.refresh();
      }
    });
  };
}

/*****************************************************************************
 * $ctrl.editable */
/**
 * @ngdoc     function
 * @name      editable
 * @methodOf  processors.controller:processorsController
 * @param     processor  Processor to look at.
 * @description
 * Determines whether or not the processor can be edited.
 *****************************************************************************/
/* @ngInject */ function editable(SERVER) {
  return function (processor) {
    return SERVER.processorConfigs.find((p) => p.type == processor.type)
      .editable;
  };
}

/*****************************************************************************
 * $ctrl.makeRowEntity */
/**
 * @ngdoc    function
 * @name     makeRowEntity
 * @methodOf csm.books.OurOrg.controller:processorsController
 * @param    {Processor} p
 * @returns  {object}    Grid row entity.
 * @description
 * Adds label properties to processor as row entity.
 *****************************************************************************/
/* @ngInject */ function makeRowEntity(NewBooksData, SERVER) {
  return (p) =>
    Object.defineProperties(new NewBooksData.Processor(p), {
      processorLabel: {
        get: function () {
          return SERVER.processorConfigs.find((c) => c.type === this.type).name;
        }
      },
      processorTypeLabel: {
        get: function () {
          return this.forPOS
            ? this.forWS
              ? 'POS/WS'
              : 'POS'
            : this.forWS
              ? 'Webstore'
              : 'External';
        }
      }
    });
}

/*****************************************************************************
 * $ctrl.openKpsMerchantDashboard */
/**
 * @ngdoc     function
 * @name      openKpsMerchantDashboard
 * @methodOf  processors.controller:processorsController
 * @description
 * Opens the KPS Merchant Dashboard in a new tab.
 *****************************************************************************/
/* @ngInject */ function openKpsMerchantDashboard($window, $ctrl) {
  return function () {
    var url = $ctrl.settings.KPSMerchantURL;
    $window.open(url, '_blank');
  };
}
